import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { ButtonSecondary, ButtonSize, IconLoader, Popup } from '@ui/src';
import ColumnHead from './column-head';
import RowWithToggle from '../column-row/column-row-with-toggle';
import { PermissionEnum } from '../../../hoc/user-roles/types';
import AllActionsRow from '../column-row/all-actions-row';
import { ColumnWithToggleTypes } from './types';

import cn from './column.module.css';

const ColumnWithToggle = ({
  isLoading,
  onChange,
  onSelectRow,
  description,
  activePermissions,
  columnData,
  title,
  isEditable,
  selectedId,
  emptyTitle,
  hasAllToggleButton = true,
  allActionsSectionButton,
  search,
  onSearch,
  error,
  popupIsOpen,
  defaultRoleSectionActions,
  actionColumn,
  values,
  setValues,
  userPermissions,
  sectionId,
}: ColumnWithToggleTypes): React.JSX.Element => {
  const [currentSectionActivePermissions, setCurrentSectionActivePermissions] =
    useState<PermissionEnum[] | undefined>();
  const [resetPopupVisible, setResetPopupVisible] = useState(false);

  useEffect(() => {
    if (
      columnData &&
      activePermissions &&
      actionColumn &&
      defaultRoleSectionActions
    ) {
      const currentActivePermissions: PermissionEnum[] = [];

      Object.entries(columnData).forEach(([key]) => {
        if (activePermissions?.find((permission) => permission === key)) {
          currentActivePermissions.push(key as PermissionEnum);
        }
      });

      setCurrentSectionActivePermissions(currentActivePermissions);
    }
  }, [columnData, activePermissions, actionColumn, defaultRoleSectionActions]);

  const onResetClick = () => {
    let nextValues = ([] as PermissionEnum[]).concat(
      values as PermissionEnum[],
    );
    const newPermission: PermissionEnum[] = [];

    defaultRoleSectionActions?.forEach((permission) => {
      if (!nextValues.find((item) => item === permission)) {
        nextValues.push(permission);
      }
    });

    currentSectionActivePermissions?.forEach((item) => {
      if (
        !defaultRoleSectionActions?.find((permission) => item === permission)
      ) {
        newPermission.push(item);
      }
    });

    if (newPermission?.length) {
      nextValues = nextValues.filter((item) =>
        newPermission?.every((permission) => item !== permission),
      );
    }

    if (
      selectedId !== 'all' &&
      userPermissions?.find((item) => item === selectedId) &&
      !nextValues?.find((item) => selectedId === item)
    ) {
      nextValues.push(selectedId as PermissionEnum);
    }

    if (setValues) {
      setValues(nextValues);
    }

    setResetPopupVisible(false);
  };

  let content: React.JSX.Element;

  const isAllToggled =
    columnData &&
    Object.keys(columnData).every((el) =>
      activePermissions?.includes(el as PermissionEnum),
    );

  if (isLoading) {
    content = (
      <div className={cn.columnLoader}>
        <IconLoader />
      </div>
    );
  } else if (error) {
    content = <div className={cn.error}>{error}</div>;
  } else {
    content = columnData ? (
      <div
        className={`${cn.columnContent} ${popupIsOpen ? cn.lessHeight : ''}`}
      >
        {hasAllToggleButton &&
          columnData &&
          Object.keys(columnData)[0] !==
            'UNASSIGNED_LIST' && (
            <div className={cn.columnButtonWrapper}>
              <ButtonSecondary
                text={isAllToggled ? 'Disable all' : 'Enable all'}
                size={ButtonSize.SMALL}
                className={`button-white ${cn.columnButton}`}
                disabled={!isEditable}
                onClick={() =>
                  isAllToggled
                    ? onChange(
                        Object.keys(columnData) as PermissionEnum[],
                        false,
                        Object.values(columnData)[0],
                      )
                    : onChange(
                        Object.keys(columnData) as PermissionEnum[],
                        true,
                        Object.values(columnData)[0],
                      )
                }
              />
            </div>
          )}
        {allActionsSectionButton && (
          <AllActionsRow
            title="All permissions in section"
            onSelectRow={onSelectRow}
            isSelectedRow={selectedId === 'all'}
            isEditable={isEditable}
          />
        )}
        {Object.entries(columnData).map(([key, value]) => (
          <RowWithToggle
            key={key}
            id={key as PermissionEnum}
            onChange={onChange}
            onSelectRow={onSelectRow}
            isSelectedRow={key === selectedId}
            isCheckedToggle={activePermissions?.includes(key as PermissionEnum)}
            item={value}
            isEditable={isEditable}
          />
        ))}
      </div>
    ) : (
      <div className={cn.emptyTitle}>{emptyTitle}</div>
    );
  }

  return (
    <div className={`${cn.column} ${onSelectRow ? '' : cn.permissionColumn}`}>
      <ColumnHead
        title={title}
        description={description}
        className={onSelectRow ? '' : cn.permissionColumn}
        onSearch={selectedId === 'all' ? onSearch : undefined}
        value={search}
        placeholder="Find permissions..."
        isEqual={Boolean(
          defaultRoleSectionActions &&
          currentSectionActivePermissions &&
          isEqual(defaultRoleSectionActions, currentSectionActivePermissions),
        )}
        actionColumn={actionColumn}
        setResetPopupVisible={setResetPopupVisible}
        columnData={columnData}
      />
      {content}
      <Popup
        title={`Reset setings ${
          selectedId === 'all' ? 'in All permissions' : ''
        } in ${
          selectedId === 'all'
            ? sectionId
                ?.split('_')
                .filter((item) => item !== 'SECTION')
                .map(
                  (item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`,
                )
                .join(' ')
            : selectedId
                ?.split('_')
                .map(
                  (item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`,
                )
                .join(' ')
        }?`}
        description=""
        onCancelClick={() => setResetPopupVisible(false)}
        onOkClick={() => onResetClick()}
        cancelButtonText="Cancel"
        okButtonText="Yes"
        isVisible={resetPopupVisible}
      />
    </div>
  );
};

export default ColumnWithToggle;
